import { WorkItemTypes, WorkItemTypeValue } from 'common/workitems/constants/workitem-types';
import { VisitorInfoModel } from 'shared/models/visitor-info/visitor-info.model';
import { CustomField } from 'shared/models/visitor-info/custom-field';
import { cloneDeep, merge } from 'lodash';
import { Injectable } from '@angular/core';
import { VisitorInfoFieldType } from 'shared/models/visitor-info/visitor-info-field';
import { VisitorData } from 'shared/models/visitor-info/visitor-data.interface';
import { Settings } from 'common/settings/settings.interface';
import { WrapupPanelService } from '../components/wrapup-panel/wrapup-panel.service';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map, mergeMap, shareReplay, startWith } from 'rxjs/operators';
import { EmailThread } from 'common/xislivechat/models/email-thread';
import { WorkItemObservableCacheService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/work-item-observable-cache.service';
import { BackendFieldConverterService } from 'shared/services/backend-field-converter/backend-field-converter.service';
import { PCacheable } from 'ngx-cacheable';
import { ChatBase } from 'common/xislivechat/models/chat-base';
import { TextChat } from 'common/xislivechat/models/text-chat';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcRestApi } from 'common/api/rest-api';
import { VisitsService } from 'common/visits/visits.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { ActionPermissionType } from 'common/common/constants/permission-type-enum';
import { UserContextService } from 'common/user-context/user-context.service';
import { CacheHelperService } from 'shared/services/cache-helper/cache-helper.service';

type CurrentUrlResult = { data: { CurrentUrl: string } };

@Injectable()
export class BcVisitorInfoBuilderService {
  private readonly _operatorId: string;
  private _settings: Settings;
  private _currentUrlObservables = {};

  constructor(private bcRestApi: BcRestApi,
              private bcSettingsService: BcSettingsService,
              private bcVisits: VisitsService,
              private _wrapupPanelService: WrapupPanelService,
              private workItemObservableCacheService: WorkItemObservableCacheService,
              private _backendFieldConverterService: BackendFieldConverterService,
              private _userPermissionService: UserPermissionService,
              private _userContextService: UserContextService,
              private _cacheHelper: CacheHelperService) {

    this._operatorId = this._userContextService.userContext.id;
  }

  getSettings(): Promise<void> {
    return this.bcSettingsService.getSettings().then((settings) => {
      this._settings = settings;
    });
  }

  observeVisitorData(workItemId, workItemType): Observable<VisitorInfoModel> {
    switch (workItemType) {
      case WorkItemTypes.CHAT:
        return this.observeVisitorDataByChatId(workItemId);
      case WorkItemTypes.EMAIL:
        return this.observeVisitorDataByEmailThreadId(workItemId);
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.SMS:
      case WorkItemTypes.ASYNC_MESSAGE:
        return this.observeVisitorDataByTextChatId(workItemId, workItemType);
    }
  }

  observeVisitorDataByChatId(chatId): Observable<VisitorInfoModel> {
    return this.workItemObservableCacheService.getWorkItemObservable(chatId, WorkItemTypes.CHAT).pipe(
      mergeMap<ChatBase, Observable<VisitorInfoModel>>(chat => {
        return combineLatest([
          from(this.createChatVisitorData(chat)),
          this.createCurrentUrlObservable(chat.VisitID, chat.VisitorID)
        ])
        .pipe(
          map<[VisitorInfoModel, CurrentUrlResult], VisitorInfoModel>(([visitorData, currentUrlData]) => {
            let newVisitorData = cloneDeep(visitorData);
            newVisitorData.fields.currentUrl.value = currentUrlData.data.CurrentUrl;
            newVisitorData.detailedData.info.currentUrl.value = currentUrlData.data.CurrentUrl;
            return newVisitorData;
          })
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  observeVisitorDataByEmailThreadId(emailThreadId): Observable<VisitorInfoModel> {
    return this.workItemObservableCacheService.getWorkItemObservable(emailThreadId, WorkItemTypes.EMAIL).pipe(
      mergeMap<EmailThread, Observable<VisitorInfoModel>>(response => {
        return from(this.createVisitorDataForEmail(response));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  observeVisitorDataByTextChatId(textChatId, textChatType): Observable<VisitorInfoModel> {
    return this.workItemObservableCacheService.getWorkItemObservable(textChatId, textChatType).pipe(
      mergeMap<TextChat, Observable<VisitorInfoModel>>(textChat => {
        return from(this.createTextChatVisitorData(textChat));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  async createChatVisitorData(chat): Promise<VisitorInfoModel> {
    const [visitorInfoModel, folderName, departmentName, website, customPreChatFields, visitorData, wrapUps] = await Promise.all([
      this.getVisitorInfoModelFromChat(chat),
      this.getFolderName(chat),
      this.getDepartmentName(chat),
      this.getWebsite(chat),
      this.getCustomPreChatFields(chat.ChatID),
      this.getVisitorDataByVisitorId(chat.VisitorID),
      this._getWrapUps(WorkItemTypes.CHAT, chat.ChatID)
    ].map(p => p.catch(() => null)));

    let visitor;
    visitor = merge({}, visitor, visitorInfoModel);
    let visitorInfoDetails = this._getDetailedDataFields(await this._getDetailedChatDataEntries(chat, visitorData));

    visitor.detailedData = merge({}, visitor.detailedData, visitorInfoDetails);
    visitor.fields.folderName.value = visitor.detailedData.routing.folderName.value = folderName;
    visitor.fields.departmentName.value = visitor.detailedData.routing.departmentName.value = departmentName;
    visitor.fields.website.value = visitor.detailedData.info.website.value = website;
    visitor.fields.customPreChatFields = visitor.detailedData.preChatForm.customPreChatFields.value = customPreChatFields;
    visitor.fields.wrapUp = visitor.detailedData.wrapUp = wrapUps;

    return visitor;
  }

  async createTextChatVisitorData(textChat): Promise<any> {
    const [visitorInfo, folderName, departmentName, wrapUps] = await Promise.all([
      this.createVisitorDataForTextChat(textChat, this._backendFieldConverterService.convertMessagingType(textChat.ChatType)),
      this.getFolderName(textChat),
      this.getDepartmentName(textChat),
      this._getWrapUps(this._backendFieldConverterService.convertMessagingType(textChat.ChatType), textChat.TextChatID)
    ].map(p => p.catch(() => null)));

    let visitor;
    visitor = merge({}, visitorInfo);
    const visitorInfoDetails = this._getDetailedDataFields(this._getDetailedTextChatDataEntries(textChat));
    visitor.detailedData = merge({}, visitor.detailedData, visitorInfoDetails);

    if (visitor.fields) {
      visitor.fields.folderName.value = visitor.detailedData.routing.folderName.value = folderName;
      visitor.fields.departmentName.value = visitor.detailedData.routing.departmentName.value = departmentName;
      visitor.fields.wrapUp = visitor.detailedData.wrapUp = wrapUps;
      if (this._isFacebook(textChat.ChatType)) {
        visitor.fields.gender.value = visitor.detailedData.general.visitorGender.value = textChat.Gender;
      }
    }

    return visitor;
  }

  createCurrentUrlObservable(visitId: string, visitorId: string): Observable<CurrentUrlResult> {
    if (!visitId || !visitorId) {
      return of({ data: { CurrentUrl: '' } });
    }
    return this.bcVisits.observeVisitInfoByVisitId(visitId, visitorId).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      startWith({ data: { CurrentUrl: '' } }) // Fallback for when no data is coming back on the query
    );
  }

  clearCurrentUrlObservable(chatId: string): void {
    if (this._currentUrlObservables[chatId]) {
      this._currentUrlObservables[chatId].unsubscribe();
      this._currentUrlObservables[chatId] = null;
    }
  }

  // TODO: this can be refactored to a simpler solution where you only add the visible fields, and the html will be simpler also
  getVisitorInfoModelFromChat(data): Promise<VisitorInfoModel> {
    return this.getSettings()
      .then((): VisitorInfoModel => {
        return {
          clientType: data.ClientType,
          displayName: data.ChatName || data.VisitEmailAddress,
          type: WorkItemTypeValue.CHAT,

          fields: {
            // Chat fields
            name: {
              value: data.ChatName,
              visible: this.isSetVisible('settings.chat.visitorInfoCard.Name'),
              translateKey: 'name'
            },
            email: {
              value: data.VisitEmailAddress,
              visible: this.isSetVisible('settings.chat.visitorInfoCard.Email'),
              translateKey: 'email'
            },
            chatLaunchUrl: {
              value: data.ChatUrl,
              visible: this.isSetVisible('settings.chat.visitorInfoCard.ChatLaunchUrl'),
              translateKey: 'chatLaunchUrl'
            },
            ip: {
              value: data.IP,
              visible: this.isSetVisible('settings.chat.visitorInfoCard.Ip'),
              translateKey: 'ip'
            },
            country: {
              value: data.CountryCode,
              visible: this.isSetVisible('settings.chat.visitorInfoCard.Country'),
              translateKey: 'country'
            },
            cityRegion: {
              value: this.getCityRegion(data.City, data.RegionCode),
              visible: this.isSetVisible('settings.chat.visitorInfoCard.CityRegion'),
              translateKey: 'cityRegion'
            },
            folderName: {
              value: '...',
              visible: this.isSetVisible('settings.chat.visitorInfoCard.FolderName'),
              translateKey: 'folderName'
            },
            departmentName: {
              value: '...',
              visible: this.isSetVisible('settings.chat.visitorInfoCard.DepartmentName'),
              translateKey: 'departmentName'
            },
            website: {
              value: '',
              visible: this.isSetVisible('settings.chat.visitorInfoCard.Website'),
              translateKey: 'website'
            },
            customField1: {
              value: data.CustomField1,
              visible: this.isSetVisible('settings.chat.visitorInfoCard.CustomField1'),
              translateKey: 'customField1'
            },
            customField2: {
              value: data.CustomField2,
              visible: this.isSetVisible('settings.chat.visitorInfoCard.CustomField2'),
              translateKey: 'customField2'
            },
            visitInfo: {
              value: data.VisitInfo,
              visible: this.isSetVisible('settings.chat.visitorInfoCard.VisitInfo'),
              translateKey: 'visitInfo'
            },
            visitRef: {
              value: data.VisitRef,
              visible: this.isSetVisible('settings.chat.visitorInfoCard.VisitRef'),
              translateKey: 'visitRef'
            },

            // Visit fields
            currentUrl: {
              value: '',
              visible: this.isSetVisible('settings.visit.visitorInfoCard.CurrentUrl'),
              translateKey: 'currentUrl'
            },

            // Nanorep context
            customFields: data.CustomFields,

            // Custom Pre-chat fields in an array
            customPreChatFields: []
          }
        };
      });
  }

  private async _getDetailedChatDataEntries(chat, visitorData) {
    visitorData = visitorData || {};

    const detailedChatDataEntries = {
      general: {
        name: { value: chat.ChatName },
        email: { value: chat.VisitEmailAddress, type: VisitorInfoFieldType.EMAIL },
        phoneNumber: { value: chat.VisitPhone, type: VisitorInfoFieldType.PHONE },
        visitInfo: { value: chat.VisitInfo },
        visitRef: { value: chat.VisitRef },
        locale: { value: visitorData.locale },
        timeZone: { value: visitorData.timeZone },
        visitorId: { value: chat.VisitorID },
        visitorToken: { value: visitorData.visitorToken }
      },
      contactAddress: {
        cityRegion: { value: this.getCityRegion(chat.City, chat.RegionCode) },
        country: { value: chat.CountryCode }
      },
      preChatForm: {
        initialQuestion: { value: chat.InitialQuestion },
        customPreChatFields: { value: [], type: VisitorInfoFieldType.CUSTOM_FIELDS }
      },
      routing: {
        departmentName: { value: '' },
        folderName: { value: '' }
      },
      info: {
        chatId: { value: chat.ChatID },
        visitId: { value: chat.VisitID },
        ip: { value: chat.IP },
        reverseIp: { value: chat.ReverseIP },
        website: { value: '' },
        clientType: { value: chat.ClientType, type: VisitorInfoFieldType.CLIENT_TYPE },
        chatLaunchUrl: { value: chat.ChatUrl },
        currentUrl: { value: '', type: VisitorInfoFieldType.LINKABLE },
        customUrl: { value: chat.CustomUrl, type: VisitorInfoFieldType.LINKABLE },
        endedReasonType: { value: chat.EndedReasonType, type: VisitorInfoFieldType.ENDED_REASON }
      },
      events: {
        created: { value: chat.Created, type: VisitorInfoFieldType.DATE },
        started: { value: chat.Started, type: VisitorInfoFieldType.DATE },
        answered: { value: chat.Answered, type: VisitorInfoFieldType.DATE },
        lastAssignedAnswered: { value: chat.LastAssignedAnswered, type: VisitorInfoFieldType.DATE },
        updated: { value: chat.Updated, type: VisitorInfoFieldType.DATE },
        totalResponseTime: { value: chat.TotalResponseTime },
        windowClosed: { value: chat.WindowClosed, type: VisitorInfoFieldType.DATE },
        closed: { value: chat.Closed, type: VisitorInfoFieldType.DATE },
        ended: { value: chat.Ended, type: VisitorInfoFieldType.DATE },
        flagged: { value: chat.Flagged }
      },
      agent: {
        agentLanguage: { value: chat.OperatorLanguageCode },
        agentMessageCount: { value: chat.OperatorMessageCount },
        visitorMessageCount: { value: chat.VisitorMessageCount },
        unRespondedMessageCount: { value: chat.UnrespondedMessageCount },
        lastMessagePersonType: { value: chat.LastMessagePersonType, type: VisitorInfoFieldType.PERSON_TYPE }
      },
      wrapUp: {
        customField1: { value: chat.CustomField1 },
        customField2: { value: chat.CustomField2 }
      },
      postChatForm: {},
      customFields: {}
    };

    const isClientCustomFieldsEnabled = await this._isClientCustomFieldsEnabled();

    if (isClientCustomFieldsEnabled) {
      detailedChatDataEntries.customFields = {
        clientCustomFields: { value: this._convertClientCustomFields(chat.CustomFields), type: VisitorInfoFieldType.CUSTOM_FIELDS }
      };
    }

    if (this._isPostChatFormEnabled(chat.OperatorID)) {
      detailedChatDataEntries.postChatForm = {
        surveyNps: { value: chat.SurveyNps },
        surveyResponsiveness: { value: chat.SurveyResponsiveness },
        surveyProfessionalism: { value: chat.SurveyProfessionalism },
        surveyKnowledge: { value: chat.SurveyKnowledge },
        surveyOverall: { value: chat.SurveyOverall },
        surveyComments: { value: chat.SurveyComments },
        experiments: { value: chat.Experiments }
      };
    }

    return detailedChatDataEntries;
  }

  private _getDetailedTextChatDataEntries(chat) {
    let detailedDataEntries;
    detailedDataEntries = {
      general: {
        name: { value: chat.ChatName },
        email: { value: chat.Email, type: VisitorInfoFieldType.EMAIL, editable: true, workItemId: chat.TextChatID },
        phoneNumber: { value: chat.PhoneNumber, type: VisitorInfoFieldType.PHONE, editable: true, workItemId: chat.TextChatID },
        visitorId: { value: chat.TextUserID }
      },
      contactAddress: {
        cityRegion: { value: this.getCityRegion(chat.City, chat.RegionCode) },
        country: { value: chat.CountryCode }
      },
      routing: {
        departmentName: { value: '' },
        folderName: { value: '' }
      },
      info: {
        textChatId: { value: chat.TextChatID },
        endedReasonType: { value: chat.EndedReasonType, type: VisitorInfoFieldType.ENDED_REASON },
        channelName: { value: chat.ChannelName }
      },
      events: {
        created: { value: chat.Created, type: VisitorInfoFieldType.DATE },
        started: { value: chat.Started, type: VisitorInfoFieldType.DATE },
        answered: { value: chat.Answered, type: VisitorInfoFieldType.DATE },
        lastAssignedAnswered: { value: chat.LastAssignedAnswered, type: VisitorInfoFieldType.DATE },
        updated: { value: chat.Updated, type: VisitorInfoFieldType.DATE },
        totalResponseTime: { value: chat.TotalResponseTime },
        closed: { value: chat.Closed, type: VisitorInfoFieldType.DATE },
        ended: { value: chat.Ended, type: VisitorInfoFieldType.DATE }
      },
      agent: {
        agentLanguage: { value: chat.LanguageCode },
        agentMessageCount: { value: chat.OperatorMessageCount },
        visitorMessageCount: { value: chat.VisitorMessageCount },
        unRespondedMessageCount: { value: chat.UnrespondedMessageCount },
        lastMessagePersonType: { value: chat.LastMessagePersonType, type: VisitorInfoFieldType.PERSON_TYPE }
      }
    };

    if (this._isFacebook(chat.ChatType)) {
      detailedDataEntries.general.visitorGender = { value: '' };
    }
    return detailedDataEntries;
  }

  private _getDetailedDataFields(detailedDataEntries) {
    Object.keys(detailedDataEntries).forEach((visitorFieldBlock) => {
      Object.keys(detailedDataEntries[visitorFieldBlock]).forEach((dataEntry) => {
        detailedDataEntries[visitorFieldBlock][dataEntry] = {
          value: detailedDataEntries[visitorFieldBlock][dataEntry].value,
          translateKey: dataEntry.toString(),
          type: detailedDataEntries[visitorFieldBlock][dataEntry].type ? detailedDataEntries[visitorFieldBlock][dataEntry].type : VisitorInfoFieldType.COMMON,
          editable: detailedDataEntries[visitorFieldBlock][dataEntry].editable,
          workItemId: detailedDataEntries[visitorFieldBlock][dataEntry].workItemId
        };
      });
    });

    return detailedDataEntries;
  }

  createVisitorDataForTextChat(data, workItemType): Promise<VisitorInfoModel> {
    let settingType;
    let workItemTypeValue;
    let settingObj: VisitorInfoModel;

    if (workItemType === WorkItemTypes.FACEBOOK) {
      settingType = 'facebook';
      workItemTypeValue = WorkItemTypeValue.FACEBOOK;
    } else if (workItemType === WorkItemTypes.SMS) {
      settingType = 'sms';
      workItemTypeValue = WorkItemTypeValue.SMS;
    } else if (workItemType === WorkItemTypes.ASYNC_MESSAGE) {
      settingType = 'asyncMessage';
      workItemTypeValue = WorkItemTypeValue.ASYNC_MESSAGE;
    } else {
      return Promise.reject();
    }

    return this.getSettings()
      .then(() => {
        settingObj = {
          clientType: data.ClientType,
          type: workItemTypeValue,
          displayName: data.ChatName,
          displayImageUrl: data.ProfilePic || data.ImageUrl,
          fields: {
            name: {
              value: data.ChatName,
              visible: this.isSetVisible(`settings.${settingType}.visitorInfoCard.Name`),
              translateKey: 'name'
            },
            email: {
              value: data.Email,
              visible: this.isSetVisible(`settings.${settingType}.visitorInfoCard.Email`),
              translateKey: 'email',
              editable: true
            },
            folderName: {
              value: '...',
              visible: this.isSetVisible(`settings.${settingType}.visitorInfoCard.FolderName`),
              translateKey: 'folderName'
            },
            departmentName: {
              value: '...',
              visible: this.isSetVisible(`settings.${settingType}.visitorInfoCard.DepartmentName`),
              translateKey: 'departmentName'
            },
            phoneNumber: {
              value: data.PhoneNumber,
              visible: this.isSetVisible(`settings.${settingType}.visitorInfoCard.PhoneNumber`),
              translateKey: 'phoneNumber',
              editable: true
            }
          }
        };

        if (workItemType === WorkItemTypes.FACEBOOK) {
          const genderObj = {
            gender: {
              value: data.Gender,
              visible: this.isSetVisible('settings.facebook.visitorInfoCard.Gender'),
              translateKey: 'visitorGender'
            }
          };

          settingObj.fields = { ...settingObj.fields, ...genderObj };
        }

        return settingObj;
      });
  }

  createVisitorDataForEmail(data): Promise<VisitorInfoModel> {
    return this.getSettings()
      .then(() => {
        return {
          type: WorkItemTypeValue.EMAIL,
          displayName: data.userEmailName || data.userEmailAddress,

          fields: {
            name: {
              value: data.userEmailName,
              visible: this.isSetVisible('settings.email.visitorInfoCard.Name')
            },
            email: {
              value: data.userEmailAddress,
              visible: this.isSetVisible('settings.email.visitorInfoCard.Email')
            },
            folderName: {
              value: data.folder.name,
              visible: this.isSetVisible('settings.email.visitorInfoCard.FolderName')
            },
            departmentName: {
              value: '...',
              visible: this.isSetVisible('settings.email.visitorInfoCard.DepartmentName')
            },
            customField1: {
              value: data.customField1,
              visible: this.isSetVisible('settings.email.visitorInfoCard.CustomField1')
            },
            customField2: {
              value: data.customField2,
              visible: this.isSetVisible('settings.email.visitorInfoCard.CustomField2')
            }
          }
        };
      });
  }

  getCityRegion(city: string, regionCode: string): string {
    const cityRegion = [];
    if (city) {
      cityRegion.push(city);
    }
    if (regionCode) {
      cityRegion.push(regionCode);
    }
    return cityRegion.join(', ');
  }

  @PCacheable()
  getVisitorDataByVisitorId(visitorId: string): Promise<VisitorData> {
    if (!visitorId) {
      return Promise.resolve(null);
    }

    return this.bcRestApi.getVisitorByVisitorId(visitorId)
      .then(visitorData => visitorData)
      .catch(error => {
        throw new Error(error);
      });
  }

  getFolderName(item): Promise<string> {
    if (!item.FolderID) {
      return Promise.resolve('');
    }
    return this._cacheHelper.getCachedFolderName(item.FolderID).toPromise();
  }

  getDepartmentName(item): Promise<string> {
    const normalizedItem = item || {};
    normalizedItem.departmentId = item && (item.DepartmentID || item.departmentId);
    if (!normalizedItem.departmentId) {
      return Promise.resolve('');
    }
    return this._cacheHelper.getCachedDepartmentName(normalizedItem.departmentId).toPromise();
  }

  getWebsite(item): Promise<string> {
    if (!item.WebsiteDefID) {
      return Promise.resolve('');
    }
    return this._cacheHelper.getCachedWebsiteName(item.WebsiteDefID).toPromise();
  }

  private _getWrapUps(type, workItemId) {
    return this._wrapupPanelService.getWrapUps(type, workItemId)
      .then((wrapUpFields) => {
        let customWrapUps;
        const builtInWrapUps = this._buildBuiltInWrapUps(wrapUpFields[0]);
        if (wrapUpFields[1] !== undefined) {
          customWrapUps = this._buildCustomWrapUps(wrapUpFields[1]);
        }
        const wrapUps = merge({}, builtInWrapUps, customWrapUps);
        return wrapUps;
      })
      .catch(() => {
        return undefined;
      });
  }

  private _isFacebook(type): boolean {
    return type === 1;
  }

  private _buildBuiltInWrapUps(builtInWrapUps) {
    let wrapUps = {};
    builtInWrapUps.forEach((builtInWrapUp) => {
      const label = (builtInWrapUp.label.split('.'))[1];
      wrapUps[label] = { value: builtInWrapUp.value, translateKey: label };
      if (builtInWrapUp.value) {
        const selectedWrapUp = builtInWrapUp.options.fields.find((wrapUp) => wrapUp.value === builtInWrapUp.value);
        wrapUps[label] = { value: selectedWrapUp.label, translateKey: label };
      }
    });
    return wrapUps;
  }

  private _buildCustomWrapUps(customWrapUps) {
    let wrapUps = {};
    customWrapUps.forEach((customWrapUp) => {
      wrapUps[customWrapUp.label || customWrapUp.name] = { value: customWrapUp.value, label: customWrapUp.label || customWrapUp.name };
      if (customWrapUp.options) {
        const selectedCustomWrapUp = customWrapUp.options.fields.find((wrapUp) => wrapUp.value === customWrapUp.value);
        if (selectedCustomWrapUp) {
          wrapUps[customWrapUp.label || customWrapUp.name] = { value: selectedCustomWrapUp.label, label: customWrapUp.label || customWrapUp.name };
        }
      }
    });
    return wrapUps;
  }

  private _convertClientCustomFields(customFields: string): CustomField[] {
    let newData: CustomField[] = [];
    if (customFields) {
      const convertedCustomFields: string[] = customFields.split(',');
      convertedCustomFields.forEach(fields => {
        let customField: string[] = fields.split('=');
        customField = this._replaceEscapeChars(customField);
        newData.push({
          value: customField[1],
          label: customField[0]
        });
      });
    }
    return newData;
  }

  private _replaceEscapeChars(customField: string[]): string[] {
    return customField.map(field => {
      return field.replace(/\\c/g, ',').replace(/\\e/g, '=');
    });
  }

  @PCacheable()
  getCustomPreChatFields(chatId): Promise<CustomField[]> {
    if (!chatId) {
      return Promise.resolve([]);
    }
    return this.bcRestApi.getCustomPreChatFields(chatId)
      .catch(error => {
        throw new Error(error);
      });
  }

  isSetVisible(key): boolean {
    const settingsMap = this.bcSettingsService.getRemapSettingsForVisitorInfoCard(this._settings);
    const settingsObj = settingsMap[key];
    return settingsObj && settingsObj.visibility;
  }

  private async _isClientCustomFieldsEnabled(): Promise<boolean> {
    const settings: Settings = await this.bcSettingsService.getSettings();
    return this.bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_VISIT_INFO_CUSTOM_FIELDS);
  }

  private _isPostChatFormEnabled(chatOperatorId: string): boolean {
    return this._isOwnChat(chatOperatorId) || this._hasPermissionToSeeOthersPostChatSurvey();
  }

  private _isOwnChat(chatOperatorId: string): boolean {
    return this._operatorId === chatOperatorId;
  }

  private _hasPermissionToSeeOthersPostChatSurvey(): boolean {
    return this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_SHOW_OTHERS_POST_CHAT_SURVEY);
  }
}
